import type { Node, Edge } from '@xyflow/react'

// ── Node Types ──────────────────────────────────────────────────────────────

export type PipelineNodeType =
  | 'trigger'
  | 'scanner'
  | 'analyzer'
  | 'workflow'
  | 'executor'
  | 'condition'
  | 'agent'

export interface TriggerNodeData {
  label: string
  triggerType: 'manual' | 'schedule' | 'webhook'
  schedule?: string // cron expression
  [key: string]: unknown
}

export interface ScannerNodeData {
  label: string
  profileId?: string
  strategyType?: string
  maxFiles?: number
  [key: string]: unknown
}

export interface AnalyzerNodeData {
  label: string
  promptId?: string
  model?: string
  temperature?: number
  [key: string]: unknown
}

export interface WorkflowNodeData {
  label: string
  workflowType?: string
  priority?: string
  autoApprove?: boolean
  [key: string]: unknown
}

export interface ExecutorNodeData {
  label: string
  agentDefinitionId?: number
  timeout?: number
  retryOnFailure?: boolean
  [key: string]: unknown
}

export interface ConditionNodeData {
  label: string
  conditionType: 'finding_count' | 'severity' | 'custom'
  operator: 'gt' | 'lt' | 'eq' | 'gte' | 'lte'
  value: string
  [key: string]: unknown
}

export interface AgentNodeData {
  label: string
  agentDefinitionId?: number
  agentName?: string
  [key: string]: unknown
}

export type PipelineNodeData =
  | TriggerNodeData
  | ScannerNodeData
  | AnalyzerNodeData
  | WorkflowNodeData
  | ExecutorNodeData
  | ConditionNodeData
  | AgentNodeData

// React Flow node types
export type TriggerNode = Node<TriggerNodeData, 'trigger'>
export type ScannerNode = Node<ScannerNodeData, 'scanner'>
export type AnalyzerNode = Node<AnalyzerNodeData, 'analyzer'>
export type WorkflowNode = Node<WorkflowNodeData, 'workflow'>
export type ExecutorNode = Node<ExecutorNodeData, 'executor'>
export type ConditionNode = Node<ConditionNodeData, 'condition'>
export type AgentNode = Node<AgentNodeData, 'agent'>

export type PipelineNode =
  | TriggerNode
  | ScannerNode
  | AnalyzerNode
  | WorkflowNode
  | ExecutorNode
  | ConditionNode
  | AgentNode

// ── Edge Types ──────────────────────────────────────────────────────────────

export type PipelineEdgeType = 'pipeline' | 'condition-true' | 'condition-false'

export interface PipelineEdgeData {
  conditionBranch?: 'true' | 'false'
  [key: string]: unknown
}

export type PipelineEdge = Edge<PipelineEdgeData>

// ── Node Visual Config ──────────────────────────────────────────────────────

export interface NodeTypeConfig {
  type: PipelineNodeType
  label: string
  description: string
  color: string       // tailwind color token (e.g. 'emerald')
  colorHex: string    // hex value for React Flow internals
  category: 'source' | 'processing' | 'output' | 'control'
  maxInputs: number
  maxOutputs: number
}

export const NODE_TYPE_CONFIGS: Record<PipelineNodeType, NodeTypeConfig> = {
  trigger: {
    type: 'trigger',
    label: 'Trigger',
    description: 'Pipeline entry point',
    color: 'emerald',
    colorHex: '#10b981',
    category: 'source',
    maxInputs: 0,
    maxOutputs: 1,
  },
  scanner: {
    type: 'scanner',
    label: 'Scanner',
    description: 'Gather context',
    color: 'cyan',
    colorHex: '#06b6d4',
    category: 'processing',
    maxInputs: 1,
    maxOutputs: 1,
  },
  analyzer: {
    type: 'analyzer',
    label: 'Analyzer',
    description: 'LLM analysis',
    color: 'blue',
    colorHex: '#3b82f6',
    category: 'processing',
    maxInputs: 1,
    maxOutputs: 1,
  },
  workflow: {
    type: 'workflow',
    label: 'Workflow',
    description: 'Create proposals',
    color: 'purple',
    colorHex: '#a855f7',
    category: 'output',
    maxInputs: 1,
    maxOutputs: 1,
  },
  executor: {
    type: 'executor',
    label: 'Executor',
    description: 'Execute workflows',
    color: 'amber',
    colorHex: '#f59e0b',
    category: 'output',
    maxInputs: 1,
    maxOutputs: 1,
  },
  condition: {
    type: 'condition',
    label: 'Condition',
    description: 'Branch on criteria',
    color: 'rose',
    colorHex: '#f43f5e',
    category: 'control',
    maxInputs: 1,
    maxOutputs: 2,
  },
  agent: {
    type: 'agent',
    label: 'Agent',
    description: 'Agent definition',
    color: 'violet',
    colorHex: '#8b5cf6',
    category: 'processing',
    maxInputs: 1,
    maxOutputs: 1,
  },
}

// ── Connection Rules ────────────────────────────────────────────────────────

export const VALID_CONNECTIONS: Record<PipelineNodeType, PipelineNodeType[]> = {
  trigger:   ['scanner', 'agent', 'condition'],
  scanner:   ['analyzer', 'condition'],
  analyzer:  ['workflow', 'condition'],
  workflow:  ['executor', 'condition'],
  executor:  ['condition'],
  condition: ['scanner', 'analyzer', 'workflow', 'executor', 'agent'],
  agent:     ['scanner', 'analyzer', 'workflow', 'executor', 'condition'],
}

// ── Pipeline Definition ─────────────────────────────────────────────────────

export interface PipelineDefinition {
  id: number
  name: string
  description?: string | null
  nodes: PipelineNode[]
  edges: PipelineEdge[]
  created_at: string
  updated_at: string
}

// ── History ─────────────────────────────────────────────────────────────────

export interface HistoryEntry {
  nodes: PipelineNode[]
  edges: PipelineEdge[]
}

// ── Default node data factories ─────────────────────────────────────────────

export function createDefaultNodeData(type: PipelineNodeType): PipelineNodeData {
  const config = NODE_TYPE_CONFIGS[type]
  const base = { label: config.label }

  switch (type) {
    case 'trigger':
      return { ...base, triggerType: 'manual' } satisfies TriggerNodeData
    case 'scanner':
      return { ...base } satisfies ScannerNodeData
    case 'analyzer':
      return { ...base, temperature: 0.3 } satisfies AnalyzerNodeData
    case 'workflow':
      return { ...base, workflowType: 'improvement', priority: 'medium', autoApprove: false } satisfies WorkflowNodeData
    case 'executor':
      return { ...base, timeout: 300, retryOnFailure: false } satisfies ExecutorNodeData
    case 'condition':
      return { ...base, conditionType: 'finding_count', operator: 'gt', value: '0' } satisfies ConditionNodeData
    case 'agent':
      return { ...base } satisfies AgentNodeData
  }
}
